/**
 * @file: releaseNotes.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { PlatformOSType } from 'react-native';

export type ChangeLogEntry = {
  type: 'feat' | 'fix' | 'perf';
  platform?: PlatformOSType;
  title: string;
  remark?: string;
};

export type ChangeSet = {
  date: string;
  changes: ChangeLogEntry[];
};

export const releaseNotes: ChangeSet[] = [
  {
    date: '2026-08-18',
    changes: [
      {
        type: 'feat',
        title: '新增智能助手，支持随页面推荐、图表回答和一键跳转',
      },
      {
        type: 'feat',
        title: '新增聊天功能，支持工单群聊、语音、图片和表情回应',
      },
      { type: 'feat', title: '新增消息中心，支持归档、免打扰、置顶和通知设置' },
      { type: 'feat', title: '新增商机中心，支持商机跟进、转化和负责人管理' },
      { type: 'feat', title: '新增预约管理，支持商机预约、到店预约和关联工单' },
      {
        type: 'feat',
        title: '保养计划改版为分组时间线，支持搜索、筛选和计划操作',
      },
      { type: 'feat', title: '完工交车支持自定义服务计划和保险到期提醒' },
      { type: 'feat', title: '新增工单搜索，支持按客户信息等条件高级检索' },
      { type: 'feat', title: '预检记录部位支持连拍模式及照片逐张选择结果' },
      { type: 'feat', title: '服务记录媒体支持批量选择并下载' },
      { type: 'feat', title: '备注等输入框支持语音输入及语音自动播放' },
      { type: 'feat', title: '产品清单支持记录成交人及成交费用汇总' },
      { type: 'feat', title: '支持多应用账号登录选择和应用切换' },
      { type: 'feat', title: '支持录入非标准车架号' },
      { type: 'feat', title: '支持分享报告到微信', platform: 'web' },
      {
        type: 'feat',
        title: '侧边栏支持折叠，工作台适配宽屏布局',
        platform: 'web',
      },
      { type: 'fix', title: '修复企业微信分享在 iOS 18 及以上无效的问题' },
      { type: 'fix', title: '修复图片标注颜色和标签菜单相关问题' },
      { type: 'fix', title: '修复登录状态在个别情况下被意外退出的问题' },
      { type: 'perf', title: '优化消息列表、图表渲染和列表加载性能' },
      { type: 'fix', title: '其他已知问题修复及体验优化' },
    ],
  },
  {
    date: '2025-09-02',
    changes: [
      { type: 'feat', title: '新增系统通知支持' },
      { type: 'feat', title: '新增多人同时施工支持' },
    ],
  },
  {
    date: '2024-12-31',
    changes: [
      { type: 'feat', title: '新增子订单支持' },
      { type: 'feat', title: '新增门店保养计划' },
      { type: 'feat', title: '全局支持完工交车时遗留问题确认和保养计划' },
      { type: 'fix', title: '其他已知问题修复及体验优化' },
    ],
  },
  {
    date: '2024-11-07',
    changes: [
      { type: 'feat', title: '新增超时工单管理' },
      { type: 'feat', title: '新增报价单与结算单功能' },
      { type: 'feat', title: '新增完工抽检功能' },
      { type: 'feat', title: '支持独立质检和完工交车流程' },
      { type: 'feat', title: '新增质检内返功能' },
      { type: 'feat', title: '质检项目允许不通过并支持填写原因' },
      { type: 'feat', title: '新增遗留问题确认功能' },
      { type: 'feat', title: '新增产品奖励明细' },
      { type: 'feat', title: '建单支持遗留问题展示和派工' },
      { type: 'feat', title: '订单信息支持更多自定义能力' },
      { type: 'feat', title: '支持更多权限控制' },
      { type: 'feat', title: '工作记录支持更多信息' },
      { type: 'fix', title: '其他已知问题修复及体验优化' },
    ],
  },
  {
    date: '2024-06-11',
    changes: [
      { type: 'feat', title: '增加产品推荐施工确认' },
      { type: 'feat', title: '新增产品推荐及施工奖励类型' },
      { type: 'fix', title: '其他已知问题修复及体验优化' },
    ],
  },
  {
    date: '2024-04-21',
    changes: [
      { type: 'feat', title: '支持查看车辆关注记录及取消车辆关注' },
      { type: 'feat', title: '新增产品推荐及施工奖励提现功能' },
      { type: 'feat', title: '支持自定义订单概要内容呈现方式' },
      { type: 'feat', title: '支持客户电话查看及拨打权限控制' },
      { type: 'fix', title: '修复微信分享报告状态显示问题' },
      { type: 'fix', title: '其他已知问题修复及体验优化' },
    ],
  },
  {
    date: '2024-03-11',
    changes: [
      {
        type: 'feat',
        title: '支持产品推荐和施工等相关功能',
      },
      {
        type: 'feat',
        title: '工作记录中支持更多记录类型',
      },
    ],
  },
  {
    date: '2023-10-30',
    changes: [
      {
        type: 'fix',
        title: '修复iPhone 15以上机型相机闪退问题',
      },
      {
        type: 'fix',
        title: '从相册选择图片/视频时如果视频过多会导致闪退',
      },
      {
        type: 'fix',
        title: '报修项目施工流程未采用系统/门店模板配置',
      },
      {
        type: 'feat',
        title: '支持门店在未配置公众号的情况下可分享报告',
      },
      {
        type: 'feat',
        title: '订单信息增加保险到期日期，并支持自动填写',
      },
      {
        type: 'feat',
        title: '支持根据客户最近订单信息自动填写本次订单相关信息',
      },
      {
        type: 'feat',
        title: '支持扫描二维码登录或切换账号',
      },
      {
        type: 'fix',
        title: '修复普通模式和演示模式切换问题',
      },
      {
        type: 'fix',
        title: '其他已知问题修复及性能优化',
      },
    ],
  },
  {
    date: '2023-09-17',
    changes: [
      {
        type: 'fix',
        title: '修复iPad拍照屏幕旋转导致的问题',
      },
      {
        type: 'fix',
        title: '修复短信验证码和微信登录异常问题',
      },
      {
        type: 'fix',
        title: '修复检测报告中项目备注及故障等级显示问题',
      },
    ],
  },
  {
    date: '2023-09-07',
    changes: [
      {
        type: 'feat',
        title: '支持通过企业微信分享报告',
      },
      {
        type: 'feat',
        title: '支持多用户账号切换',
      },
      {
        type: 'feat',
        title: '施工项目派工时支持记住并沿用首次派工信息',
      },
      {
        type: 'fix',
        title: '修复完工质检时默认勾选项目没有选中的问题',
      },
      {
        type: 'feat',
        title: '支持自定义时间/日期扩展订单属性',
      },
      {
        type: 'fix',
        title: '其他一些已知问题修复和优化',
      },
    ],
  },
  {
    date: '2023-07-31',
    changes: [
      {
        type: 'fix',
        title: '服务项目按拼音首字母搜索时分区大小写的问题',
      },
      {
        type: 'fix',
        title: '施工项目搜索结果中"+"按钮不可用问题',
      },
      {
        type: 'fix',
        title: '工单完工或返单时看板没有自动刷新问题',
      },
      {
        type: 'fix',
        title: '交车质检页面删除第一个质检项目不易操作问题',
      },
      {
        type: 'fix',
        title: '优化从相册选择图片以后存在较长延迟的问题',
      },
      {
        type: 'fix',
        title: '修改订单信息时部分字段必须填写的问题',
      },
      {
        type: 'fix',
        title: '无法一键清除暂存的建单草稿问题',
      },
      {
        type: 'fix',
        title: '图片标注默认颜色与故障或项目类型不匹配问题',
      },
      {
        type: 'fix',
        title: '快速点击拍照按钮会连续多拍的问题',
      },
      {
        type: 'fix',
        title: '手指在录制按钮外松开时视频没有停止录制的问题',
      },
      {
        type: 'fix',
        title: '订单预览和详情查看页中仪表盘照片不能预览问题',
      },
      {
        type: 'fix',
        title: '预检报告中正常外观部位结果显示不正常问题',
      },
      {
        type: 'feat',
        title: '服务记录页面重新支持扫码关注功能',
      },
      {
        type: 'feat',
        title: '支持自动在本地保存自定义施工项目的功能',
      },
      {
        type: 'feat',
        title: '支持施工项目工序自定义',
      },
      {
        type: 'feat',
        title: '支持新建订单表单字段自定义能力',
      },
      {
        type: 'feat',
        title: '预检/检测/施工/质检等全面支持多图选择并上传',
      },
      {
        type: 'feat',
        title: '对某些特殊应用场景的支持',
      },
      {
        type: 'feat',
        title: '门店/历史看板增加刷新功能',
        platform: 'web',
      },
      {
        type: 'fix',
        title: '图片加载占位符无法填充问题',
        platform: 'web',
      },
      {
        type: 'fix',
        title: '其他一些已知问题修复和优化',
      },
    ],
  },
  {
    date: '2023-07-20',
    changes: [
      {
        type: 'feat',
        title: '服务记录支持查看包括报告、遗留问题、媒体图库、服务历史等',
      },
      {
        type: 'feat',
        title: '任务详情页支持查看遗留问题、图库、服务记录和完整报告预览',
      },
      {
        type: 'feat',
        title:
          '改进建单流程，支持建单时进行仪表盘拍摄、环检、派工等，并支持自定义订单字段',
      },
      {
        type: 'feat',
        title: '建单支持老客户信息自动填写',
      },
      {
        type: 'feat',
        title: '建单支持需要输入车牌即可让客户关注',
      },
      {
        type: 'feat',
        title: '建单新增草稿和恢复功能',
      },
      {
        type: 'feat',
        title: '用户界面优化以自适应不同尺寸大小的屏幕',
      },
      {
        type: 'feat',
        title: '支持门店自定义服务协议',
      },
      {
        type: 'feat',
        title: '支持手机端打印接车单',
      },
      {
        type: 'feat',
        title: '支持通过短信分享报告',
      },
      {
        type: 'feat',
        title: '支持缓存大小查看以及缓存清除',
      },
      {
        type: 'feat',
        title: '任务详情和服务记录支持报告分享及客户查阅情况状态显示',
      },
      {
        type: 'feat',
        title: '支持已完工订单维修返单',
      },
      {
        type: 'feat',
        title: '施工项目增加派工功能',
      },
      {
        type: 'feat',
        title: '支持应用内跳转小程序门店看板',
      },
      {
        type: 'perf',
        title: '优化内存使用',
      },
      {
        type: 'feat',
        title: 'iPad页面内容元素及布局调整和优化',
      },
      {
        type: 'fix',
        title: '修复预检图片尚未全部上传完成之前推送报告导致图片丢失',
      },
      {
        type: 'fix',
        title: '修复施工反馈页面所解决问题未自动选择',
      },
      {
        type: 'fix',
        title: '修复输入框系统弹出菜单中文字内容为英文问题',
      },
      {
        type: 'fix',
        title: '操作权限问题导致用户被强制退出登录的问题',
      },
      {
        type: 'fix',
        title: '其他已知问题修复及优化',
      },
    ],
  },
  {
    date: '2023-04-06',
    changes: [
      {
        type: 'feat',
        title: '车牌和VIN拍照识别时支持旋转锁定',
      },
      {
        type: 'feat',
        title: '支持按检测结果、施工结果以及业务类型匹配不同的质检流程',
      },
      {
        type: 'feat',
        title: '重新设计的更简单的门店看板和历史看板',
      },
    ],
  },
  {
    date: '2022-09-01',
    changes: [
      {
        type: 'feat',
        title: '任务详情和接车预检页面已适配iPad全屏模式',
      },
    ],
  },
  {
    date: '2022-08-24',
    changes: [
      {
        type: 'feat',
        title: '门店员工选择界面支持按关键词或首字母搜索',
      },
      {
        type: 'feat',
        title: '支持检测流程派工时对检测模板进行预览',
      },
      {
        type: 'feat',
        title: '支持检测流程派工界面按部位关键词进行模板筛选',
      },
      {
        type: 'feat',
        title: '预览预检报告时确保仪表照片已拍摄并成功上传',
      },
      {
        type: 'fix',
        title: '修复检测流程必检项目验证问题',
      },
      {
        type: 'fix',
        title: '修复取消检测流程后导致无法推送整个检测报告的问题',
      },
      {
        type: 'fix',
        title: '修复从相册选择照片后图片被过度裁剪的问题',
      },
    ],
  },
  {
    date: '2022-08-23',
    changes: [
      {
        type: 'feat',
        title: '门店看板支持查看仅与当前用户相关的工单',
      },
      {
        type: 'feat',
        title: '常规检测支持取消部位检测结果',
      },
      {
        type: 'feat',
        title: '常规检测支持重新派工',
      },
      {
        type: 'fix',
        title: '其他已知问题修复',
      },
    ],
  },
  {
    date: '2022-08-18',
    changes: [
      {
        type: 'feat',
        title: '建单时支持服务标签以及填写更多客户信息',
      },
      {
        type: 'fix',
        title: '修复交车模板无法正确使用的问题',
      },
      {
        type: 'fix',
        title: '其他一些小的问题修复',
      },
    ],
  },
  {
    date: '2022-08-17',
    changes: [
      {
        type: 'feat',
        title: '建单或修改订单信息时支持特殊车牌',
      },
      {
        type: 'feat',
        title: '支持未检测时的交车模板选择',
      },
    ],
  },
  {
    date: '2022-08-16',
    changes: [
      {
        type: 'feat',
        title: '仅在开启的时候显示智能硬件专检',
      },
      {
        type: 'feat',
        title: '全车检测支持必检项目',
      },
    ],
  },
  {
    date: '2022-07-30',
    changes: [
      {
        type: 'feat',
        title: '完工交车支持预期选项设置',
      },
      {
        type: 'feat',
        title: '支持普通工作模式和演示模式切换',
      },
    ],
  },
  {
    date: '2022-07-01',
    changes: [
      {
        type: 'fix',
        title: '修复交车完工后返回首页后某些时候列表不刷新并不能点击的问题',
      },
      {
        type: 'feat',
        title: '报告预览和查看页面增加显示服务顾问、技师或智检员',
      },
      {
        type: 'feat',
        title: '自定义故障支持同时显示预定义故障影响和技师备注',
      },
      {
        type: 'feat',
        title: '支持删除自定义故障部位及相关故障信息',
      },
      {
        type: 'fix',
        title: '修复自定义故障显示在非所属的检测流程的问题',
      },
      {
        type: 'feat',
        title: '视频播放界面增加分享功能，与图片分享类似',
      },
      {
        type: 'fix',
        title: '修复长按录制最大长度视频结束后，视频丢失不能上传的问题',
      },
      {
        type: 'feat',
        title: '优化iPad拍照、录制视频和图片标注等功能',
      },
      {
        type: 'fix',
        title: '修复iPad上屏幕自动旋转开启时无法拍摄车牌或VIN进行识别的问题',
      },
      {
        type: 'feat',
        title: '工作进度中支持显示报告推送相关的事件',
      },
      {
        type: 'feat',
        title: '二维码关注分享页面增加直接分享小程序报告的功能',
      },
      {
        type: 'feat',
        title: '报告预览或查看页面增加分享报告的功能',
      },
      {
        type: 'feat',
        title: '预检/检测/施工完成后增加操作完成及分享报告页面',
      },
      {
        type: 'fix',
        title: '修复在某些情况下浮动按钮存在遮挡页面内容的问题',
      },
      {
        type: 'feat',
        title: '预检车辆外观拍摄相机界面增加显示相关部位名称并支持修改部位',
      },
      {
        type: 'fix',
        title: '修改通过链接(Universal Links)打开应用存在的一些问题',
      },
      {
        type: 'fix',
        title: '优化建单页面编辑订单备注输入框显示和交互',
      },
      {
        type: 'fix',
        title: '修复自定义故障备注输入框无法换行的问题',
      },
      {
        type: 'feat',
        title: '实现视频压缩、缩略图生成以优化视频上传速度',
      },
      {
        type: 'fix',
        title: '其他一些已知问题修复',
      },
      {
        type: 'feat',
        title: '支持删除用户账号',
      },
    ],
  },
  {
    date: '2022-06-17',
    changes: [
      {
        type: 'feat',
        title: '支持在检测报告中显示指标检测结果数值',
      },
      {
        type: 'feat',
        title: '优化强制拍照时只要任意检测项目有图片或视频即可',
      },
      {
        type: 'feat',
        title: '正常选项下拍照时不添加默认图片标注',
      },
      {
        type: 'feat',
        title: '预检增加前部记录和后部记录功能',
      },
      {
        type: 'feat',
        title: '增加对选择并上传相册视频的支持',
      },
      {
        type: 'feat',
        title: '确保必须至少添加一个工序才能完成施工',
      },
    ],
  },
  {
    date: '2022-06-13',
    changes: [
      {
        type: 'fix',
        title: '无法修改订单服务类型',
      },
      {
        type: 'fix',
        title: '预计交车时间无法保存/更新及显示',
      },
      {
        type: 'feat',
        title: '建单时尝试自动获取历史车辆信息无需重复填写',
      },
      {
        type: 'feat',
        title: '增加了对于需要强制拍照的检测结果的检查',
      },
      {
        type: 'feat',
        title: '修改检测值时自动更新检测结果选项',
      },
      {
        type: 'feat',
        title: '开始预检时取消了预检确认页面优化体验',
      },
      {
        type: 'fix',
        title: '其他一些小的修复',
      },
    ],
  },
  {
    date: '2022-06-02',
    changes: [
      {
        type: 'fix',
        title: '修改订单信息时无法修改服务顾问',
      },
      {
        type: 'feat',
        title: '如果当前用户为服务顾问, 建单时将成为默认服务顾问',
      },
      {
        type: 'feat',
        title: '实现各种拍照场景下的默认图片标注',
      },
      {
        type: 'feat',
        title: '为任务详情页面中各工序添加明显状态标识',
      },
      {
        type: 'fix',
        title: '施工项目完成后施工工序状态未同步更新',
      },
      {
        type: 'feat',
        title: '优化交车检查时未推送施工报告提醒及相关快捷操作',
      },
      {
        type: 'feat',
        title: '优化扫描车牌或VIN建单时跳转到已存在工单页面的逻辑',
      },
      {
        type: 'fix',
        title: '交车检查时相关后续施工项目没有同步导致无法交车',
      },
      {
        type: 'fix',
        title: '未做检测时交车检查依然包含有检测相关交车项目',
      },
    ],
  },
];
